/**
 * Sport definitions.
 *
 * Operator-facing on purpose: each entry describes what a CLUB configures for
 * that sport, not live data from any facility.
 */

export interface SportStat {
  k: string;
  v: string;
}

export interface Sport {
  id: string;
  name: string;
  tag: string;
  blurb: string;
  /** CSS aspect-ratio for the court diagram, e.g. "44 / 20" */
  ratio: string;
  /** inline styles for each court marking, positioned inside the court box */
  lines: string[];
  stats: SportStat[];
  /** 24x24 stroke icon, drawn inside the sport button */
  glyph: string;
}

export const SPORTS: Sport[] = [
  {
    id: 'pickleball',
    name: 'Pickleball',
    tag: 'FASTEST GROWING CATEGORY',
    blurb:
      'Kitchen-line court maps, short high-turnover slots and open-play formats. Built for the sport that fills a facility on a weeknight.',
    ratio: '44 / 20',
    lines: [
      'left:50%;top:0;width:2px;height:100%;background:rgba(200,255,60,.55)',
      'left:31%;top:0;width:1px;height:100%',
      'left:69%;top:0;width:1px;height:100%',
      'left:0;top:50%;width:31%;height:1px',
      'left:69%;top:50%;width:31%;height:1px',
    ],
    stats: [
      { k: 'COURT SETUP', v: 'Indoor or outdoor' },
      { k: 'SLOT LENGTHS YOU SET', v: '60 or 90 min' },
      { k: 'FORMATS SUPPORTED', v: 'Open play, ladder, league' },
      { k: 'OPERATOR CONTROLS', v: 'Peak pricing, waitlist' },
    ],
    glyph: '<ellipse cx="10" cy="9" rx="6" ry="7"/><path d="M12.5 15.5 17 21"/>',
  },
  {
    id: 'tennis',
    name: 'Tennis',
    tag: 'CLUB CLASSIC',
    blurb:
      'Member priority windows, floodlight surcharges and coach blocks held back for lessons — all set per court, per season.',
    ratio: '78 / 36',
    lines: [
      'left:50%;top:0;width:2px;height:100%;background:rgba(200,255,60,.55)',
      'left:0;top:11%;width:100%;height:1px',
      'left:0;top:89%;width:100%;height:1px',
      'left:26%;top:11%;width:1px;height:78%',
      'left:74%;top:11%;width:1px;height:78%',
      'left:26%;top:50%;width:48%;height:1px',
    ],
    stats: [
      { k: 'COURT SETUP', v: 'Singles and doubles lines' },
      { k: 'SLOT LENGTHS YOU SET', v: '60 min, extendable' },
      { k: 'FORMATS SUPPORTED', v: 'Casual, box league, coaching' },
      { k: 'OPERATOR CONTROLS', v: 'Member windows, floodlight fees' },
    ],
    glyph:
      '<circle cx="12" cy="12" r="8"/><path d="M4.5 8c5 2 10 2 15 0M4.5 16c5-2 10-2 15 0"/>',
  },
  {
    id: 'badminton',
    name: 'Badminton',
    tag: 'HIGH TURNOVER',
    blurb:
      'A hall of courts booked in half-hour blocks, with shuttle hire and equipment added to the booking before the player leaves the app.',
    ratio: '44 / 20',
    lines: [
      'left:50%;top:0;width:2px;height:100%;background:rgba(200,255,60,.55)',
      'left:36%;top:0;width:1px;height:100%',
      'left:64%;top:0;width:1px;height:100%',
      'left:0;top:50%;width:36%;height:1px',
      'left:64%;top:50%;width:36%;height:1px',
      'left:6%;top:0;width:1px;height:100%',
      'left:94%;top:0;width:1px;height:100%',
    ],
    stats: [
      { k: 'COURT SETUP', v: 'Multi-court hall, booked singly' },
      { k: 'SLOT LENGTHS YOU SET', v: '30 or 60 min' },
      { k: 'FORMATS SUPPORTED', v: 'Casual, club night, coaching' },
      { k: 'OPERATOR CONTROLS', v: 'Equipment hire, block booking' },
    ],
    glyph: '<path d="M12 4l4 6-4 10-4-10z"/><path d="M8 10h8"/>',
  },
  {
    id: 'basketball',
    name: 'Basketball',
    tag: 'FULL AND HALF COURT',
    blurb:
      'Split the floor for half-court rentals or lock the whole hall for league night, with team rosters attached to every booking.',
    ratio: '94 / 50',
    lines: [
      'left:50%;top:0;width:2px;height:100%;background:rgba(200,255,60,.55)',
      'left:50%;top:50%;width:92px;height:92px;margin:-46px 0 0 -46px;border:1px solid rgba(255,255,255,.22);border-radius:50%;background:transparent',
      'left:0;top:26%;width:19%;height:48%;border:1px solid rgba(255,255,255,.22);background:transparent',
      'left:81%;top:26%;width:19%;height:48%;border:1px solid rgba(255,255,255,.22);background:transparent',
      'left:-6%;top:12%;width:28%;height:76%;border:1px solid rgba(255,255,255,.16);border-radius:50%;background:transparent',
      'left:78%;top:12%;width:28%;height:76%;border:1px solid rgba(255,255,255,.16);border-radius:50%;background:transparent',
    ],
    stats: [
      { k: 'COURT SETUP', v: 'Full court or two halves' },
      { k: 'SLOT LENGTHS YOU SET', v: '60 or 120 min' },
      { k: 'FORMATS SUPPORTED', v: 'Rentals, leagues, training' },
      { k: 'OPERATOR CONTROLS', v: 'Team rosters, hall lockout' },
    ],
    glyph: '<circle cx="12" cy="12" r="8"/><path d="M12 4v16M4 12h16"/>',
  },
  {
    id: 'football',
    name: 'Football',
    tag: '5, 7 OR 11 A SIDE',
    blurb:
      'Pitches, cages and turf with weather rules, deposit holds and squad invitations sent the moment a booking is confirmed.',
    ratio: '105 / 68',
    lines: [
      'left:50%;top:0;width:2px;height:100%;background:rgba(200,255,60,.55)',
      'left:50%;top:50%;width:104px;height:104px;margin:-52px 0 0 -52px;border:1px solid rgba(255,255,255,.22);border-radius:50%;background:transparent',
      'left:0;top:21%;width:16%;height:58%;border:1px solid rgba(255,255,255,.22);background:transparent',
      'left:84%;top:21%;width:16%;height:58%;border:1px solid rgba(255,255,255,.22);background:transparent',
      'left:0;top:36%;width:6%;height:28%;border:1px solid rgba(255,255,255,.18);background:transparent',
      'left:94%;top:36%;width:6%;height:28%;border:1px solid rgba(255,255,255,.18);background:transparent',
    ],
    stats: [
      { k: 'PITCH SETUP', v: 'Full pitch or split cages' },
      { k: 'SLOT LENGTHS YOU SET', v: '60 min, extendable' },
      { k: 'FORMATS SUPPORTED', v: '5, 7 and 11 a side' },
      { k: 'OPERATOR CONTROLS', v: 'Deposits, weather rules' },
    ],
    glyph:
      '<circle cx="12" cy="12" r="8"/><path d="M12 8l3.6 2.6-1.4 4.2h-4.4L8.4 10.6z"/>',
  },
  {
    id: 'golf',
    name: 'Golf',
    tag: 'BAYS AND SIMULATORS',
    blurb:
      'Range bays and simulator rooms booked by the hour, with ball credit, lesson add-ons and no-show rules applied automatically.',
    ratio: '3 / 2',
    lines: [
      'left:0;top:78%;width:100%;height:2px;background:rgba(200,255,60,.5)',
      'left:50%;top:78%;width:340px;height:340px;margin-left:-170px;border:1px solid rgba(255,255,255,.14);border-radius:50%;background:transparent',
      'left:50%;top:78%;width:220px;height:220px;margin-left:-110px;border:1px solid rgba(255,255,255,.18);border-radius:50%;background:transparent',
      'left:50%;top:78%;width:110px;height:110px;margin-left:-55px;border:1px solid rgba(255,255,255,.22);border-radius:50%;background:transparent',
      'left:25%;top:78%;width:1px;height:22%',
      'left:75%;top:78%;width:1px;height:22%',
    ],
    stats: [
      { k: 'BAY SETUP', v: 'Range bays and sim rooms' },
      { k: 'SLOT LENGTHS YOU SET', v: '30, 60 or 90 min' },
      { k: 'FORMATS SUPPORTED', v: 'Practice, lessons, sim rounds' },
      { k: 'OPERATOR CONTROLS', v: 'Ball credit, no-show rules' },
    ],
    glyph: '<path d="M8 20V4l9 4-9 4"/><path d="M6 20h7"/>',
  },
  {
    id: 'squash',
    name: 'Squash',
    tag: 'FOUR-WALL COURTS',
    blurb:
      'Member ladders, racket hire and a waiting list that offers a freed slot to the next player the moment a booking is cancelled.',
    ratio: '64 / 98',
    lines: [
      'left:0;top:56%;width:100%;height:2px;background:rgba(200,255,60,.5)',
      'left:50%;top:56%;width:1px;height:44%',
      'left:0;top:56%;width:26%;height:22%;border:1px solid rgba(255,255,255,.22);background:transparent',
      'left:74%;top:56%;width:26%;height:22%;border:1px solid rgba(255,255,255,.22);background:transparent',
      'left:0;top:24%;width:100%;height:1px',
    ],
    stats: [
      { k: 'COURT SETUP', v: 'Four-wall, glass back' },
      { k: 'SLOT LENGTHS YOU SET', v: '45 min' },
      { k: 'FORMATS SUPPORTED', v: 'Ladder, casual, coaching' },
      { k: 'OPERATOR CONTROLS', v: 'Auto waitlist, racket hire' },
    ],
    glyph: '<ellipse cx="10" cy="9" rx="5.5" ry="6.5"/><path d="M12.5 15 17 20.5"/>',
  },
  {
    id: 'tabletennis',
    name: 'Table Tennis',
    tag: 'TABLES BY THE HOUR',
    blurb:
      'Tables booked individually or as a block for club night, with bat hire and a tournament mode for weekend events.',
    ratio: '274 / 152',
    lines: [
      'left:50%;top:0;width:2px;height:100%;background:rgba(200,255,60,.55)',
      'left:0;top:50%;width:100%;height:1px',
      'left:0;top:0;width:100%;height:1px;opacity:.4',
      'left:0;bottom:0;width:100%;height:1px;opacity:.4',
    ],
    stats: [
      { k: 'TABLE SETUP', v: 'Per table or whole room' },
      { k: 'SLOT LENGTHS YOU SET', v: '30 or 60 min' },
      { k: 'FORMATS SUPPORTED', v: 'Casual, club night' },
      { k: 'OPERATOR CONTROLS', v: 'Bat hire, block booking' },
    ],
    glyph:
      '<circle cx="9.5" cy="9.5" r="6"/><path d="M13.5 13.5 18 19.5"/><circle cx="18" cy="7" r="2"/>',
  },
];
